import { ChangeEvent, useCallback, useMemo, useState } from 'react'
import {
  useController,
  useFormContext,
  FieldValues,
  FieldPath
} from 'react-hook-form'
import { Input, Label, Select, SelectOption } from '@pay-com/components'
import { WorldFlag } from '@pay-com/icons'
import { getCountryCallingCode, getCountries } from 'libphonenumber-js'
import { useGetAllCountriesQuery } from 'generated/graphql'
import { ICustomFieldComponentProps } from 'types'
import { getErrorMessage } from 'utils/get-error-message'
import { useTranslation } from 'react-i18next'
import {
  CountryPhoneCodeOption,
  getCountryPhoneCodeOption
} from './get-country-phone-code-option'

type PhoneNumberFieldProps = ICustomFieldComponentProps<
  FieldValues,
  FieldPath<FieldValues>,
  { lockedCountry?: string }
>

const getCallingCode = (alpha2: string) => {
  try {
    return getCountryCallingCode(
      alpha2 as Parameters<typeof getCountryCallingCode>[0]
    )
  } catch {
    return ''
  }
}

const splitPhoneValue = (
  value: string | null | undefined,
  preferredCountry = 'US'
) => {
  if (!value) return { countryAlpha2: preferredCountry, number: '' }

  const withoutPlus = value.replace(/^\+/, '')

  const matchedCountries = getCountries().filter(country => {
    const code = getCountryCallingCode(country)
    return withoutPlus.startsWith(code)
  })

  if (matchedCountries.length > 0) {
    const best =
      matchedCountries.find(c => c === preferredCountry) ?? matchedCountries[0]
    const code = getCountryCallingCode(best)
    const number = withoutPlus.slice(code.length)
    return { countryAlpha2: best, number }
  }

  return { countryAlpha2: preferredCountry, number: withoutPlus }
}

const emptyCountries: { name: string; alpha2: string }[] = []

export const PhoneNumberField = ({
  name,
  label,
  lockedCountry
}: PhoneNumberFieldProps) => {
  const { control } = useFormContext()
  const { field, fieldState } = useController({ name, control })
  const { t } = useTranslation('errors')

  const { data } = useGetAllCountriesQuery()
  const countries = data?.countries ?? emptyCountries

  const [countryAlpha2, setCountryAlpha2] = useState(
    () => lockedCountry ?? splitPhoneValue(field.value).countryAlpha2
  )
  const [number, setNumber] = useState(
    () => splitPhoneValue(field.value).number
  )

  const countryOptions = useMemo(
    () =>
      countries
        .map(country => getCountryPhoneCodeOption(country))
        .filter((option): option is CountryPhoneCodeOption => option !== null),
    [countries]
  )

  const selectedCountryOption = useMemo(
    () => countryOptions.find(option => option.value === countryAlpha2) ?? null,
    [countryOptions, countryAlpha2]
  )

  const handleCountryChange = useCallback(
    (alpha2: string) => {
      if (lockedCountry) return
      setCountryAlpha2(alpha2)
      setNumber(prev => {
        if (!prev) {
          field.onChange('')
        } else {
          const callingCode = getCallingCode(alpha2)
          field.onChange(`+${callingCode}${prev}`)
        }
        return prev
      })
    },
    [lockedCountry, field]
  )

  const handleNumberChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const num = e.target.value
      setNumber(num)
      setCountryAlpha2(prev => {
        if (!num) {
          field.onChange('')
        } else {
          const callingCode = getCallingCode(prev)
          field.onChange(`+${callingCode}${num}`)
        }
        return prev
      })
    },
    [field]
  )

  const hasError = !!fieldState.error

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <Label label={label} disabled={field.disabled} error={hasError} />
      )}
      <div className="flex gap-1">
        <Select
          value={selectedCountryOption}
          options={countryOptions}
          onChange={(option: SelectOption | null) => {
            if (option) handleCountryChange(option.value)
          }}
          className="w-[6.875rem]"
          // @ts-expect-error react-select supports ReactNode at runtime; types restrict to string
          placeholder={<WorldFlag className="h-[0.9375rem] w-[1.3125rem]" />}
          searchByLabelAndByValue
          disabled={!!lockedCountry}
          error={hasError}
          formatOptionLabel={(
            option: CountryPhoneCodeOption,
            meta: { context: 'menu' | 'value' }
          ) => (meta.context === 'value' ? option.selectedLabel : option.label)}
          styles={{
            menu: base => ({ ...base, width: '200px' })
          }}
        />
        <Input
          placeholder="Phone number"
          value={number}
          onChange={handleNumberChange}
          error={hasError}
          errorMessage={getErrorMessage(t, fieldState.error?.message)}
          containerProps={{ className: 'w-full' }}
        />
      </div>
    </div>
  )
}
